import React, { useState } from 'react';
import { Crown, Users, GraduationCap, Bot, Heart, Settings, Check, ArrowRight } from 'lucide-react';
import { solutions } from '../data/mock';
import { Button } from './ui/button';

const iconMap = {
  Crown: Crown,
  Users: Users,
  GraduationCap: GraduationCap,
  Bot: Bot,
  Heart: Heart,
  Settings: Settings
};

const Solutions = () => {
  const [activeSolution, setActiveSolution] = useState(0);

  const scrollToConsultation = () => {
    const element = document.querySelector('#consultation');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="solutions" className="py-16 sm:py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-cyan-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
            What We Offer
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
            Solutions That Develop People
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-violet-400">
              And Strengthen Performance
            </span>
          </h2>
          <p className="text-slate-400 text-base sm:text-lg max-w-3xl mx-auto">
            From leadership and workforce development to AI integration—practical solutions 
            that create real results for your organization.
          </p>
        </div>

        {/* Solutions Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-12">
          {solutions.map((solution, index) => {
            const IconComponent = iconMap[solution.icon];
            const isActive = activeSolution === index;
            
            return (
              <div
                key={solution.id}
                onClick={() => setActiveSolution(index)}
                className={`group relative p-5 sm:p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-br from-cyan-500/15 to-violet-500/10 border-cyan-500/40 shadow-lg shadow-cyan-500/5'
                    : 'bg-white/[0.02] border-white/10 hover:border-white/20 hover:bg-white/[0.04]'
                } border`}
              >
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 ${
                  isActive
                    ? 'bg-cyan-500 text-white'
                    : 'bg-white/5 text-cyan-400 group-hover:bg-white/10'
                }`}>
                  <IconComponent className="h-6 w-6" />
                </div>

                {/* Title */}
                <h3 className={`text-lg font-bold mb-2 transition-colors ${
                  isActive ? 'text-cyan-400' : 'text-white group-hover:text-cyan-400'
                }`}>
                  {solution.title}
                </h3>

                {/* Description */}
                <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                  {solution.description}
                </p>

                {/* Features */}
                <ul className={`space-y-2 transition-all duration-300 ${
                  isActive ? 'opacity-100' : 'opacity-70'
                }`}>
                  {solution.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                        isActive ? 'text-cyan-400' : 'text-slate-600'
                      }`} />
                      <span className="text-slate-400 text-xs sm:text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Active Indicator */}
                {isActive && (
                  <div className="absolute top-4 right-4">
                    <span className="w-2 h-2 rounded-full bg-cyan-400 block animate-pulse" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={scrollToConsultation}
            className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 py-6 text-lg transition-all duration-300"
          >
            Explore Solutions for Your Organization
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Solutions;
